import { useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import "@/App.css";
import AdminApp from "@/admin/AdminApp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DOWNLOAD_URL = `${BACKEND_URL}/api/download/Part_A.zip`;

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin/*" element={<AdminApp />} />
        <Route path="*" element={<DownloadPage />} />
      </Routes>
    </BrowserRouter>
  );
}

function DownloadPage() {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(DOWNLOAD_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // ignore
    }
  };

  return (
    <div
      data-testid="download-page"
      style={{
        minHeight: "100vh",
        backgroundColor: "#0f172a",
        color: "#e2e8f0",
        fontFamily:
          "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
        padding: "32px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div style={{ maxWidth: 760, width: "100%" }}>
        <header
          style={{
            textAlign: "center",
            marginBottom: 40,
          }}
        >
          <div
            style={{
              fontSize: 13,
              letterSpacing: 2,
              color: "#94a3b8",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Health Consultancy RPA
          </div>
          <h1
            data-testid="page-title"
            style={{
              fontSize: "2.5rem",
              fontWeight: 700,
              margin: 0,
              color: "#f1f5f9",
            }}
          >
            Part A — Robot Installer
          </h1>
          <p
            style={{
              marginTop: 12,
              color: "#94a3b8",
              fontSize: 16,
            }}
          >
            Download the local RPA robot for your Windows laptop.
          </p>
        </header>

        <div
          style={{
            backgroundColor: "#1e293b",
            border: "1px solid #334155",
            borderRadius: 12,
            padding: 32,
            marginBottom: 24,
          }}
        >
          <a
            data-testid="download-button"
            href={DOWNLOAD_URL}
            style={{
              display: "block",
              backgroundColor: "#10b981",
              color: "white",
              textAlign: "center",
              padding: "20px 24px",
              borderRadius: 10,
              textDecoration: "none",
              fontSize: 20,
              fontWeight: 600,
              boxShadow: "0 4px 14px rgba(16,185,129,0.35)",
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
            onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            ⬇  Download Part_A.zip  (≈ 50 KB)
          </a>

          <div
            style={{
              marginTop: 16,
              fontSize: 13,
              color: "#94a3b8",
              textAlign: "center",
            }}
          >
            If the button doesn't work, copy this link manually:
          </div>
          <div
            style={{
              marginTop: 8,
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <code
              data-testid="download-link-text"
              style={{
                background: "#0f172a",
                border: "1px solid #334155",
                padding: "8px 12px",
                borderRadius: 6,
                fontSize: 12,
                color: "#cbd5e1",
                wordBreak: "break-all",
              }}
            >
              {DOWNLOAD_URL}
            </code>
            <button
              data-testid="copy-link-button"
              onClick={copyLink}
              style={{
                background: copied ? "#10b981" : "#334155",
                color: "white",
                border: "none",
                padding: "8px 14px",
                borderRadius: 6,
                fontSize: 12,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {copied ? "✓ Copied" : "Copy"}
            </button>
          </div>
        </div>

        <Section title="Installation steps (Windows)">
          <Step n={1} title="Install Python (if you don't have it)">
            Visit{" "}
            <a
              href="https://www.python.org/downloads/"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#60a5fa" }}
            >
              python.org/downloads
            </a>
            , download Python 3.12, run the installer, and
            {" "}<b>tick "Add Python to PATH"</b> at the bottom of the first screen.
          </Step>

          <Step n={2} title="Download the ZIP">
            Click the green button above. Save <code>Part_A.zip</code> to your
            Desktop (or anywhere convenient).
          </Step>

          <Step n={3} title="Unzip">
            Right-click <code>Part_A.zip</code> → <b>Extract All…</b> → choose a
            location like <code>C:\Part_A</code>. You'll get a folder called
            {" "}<code>part_a</code>.
          </Step>

          <Step n={4} title="Open the folder & run the installer">
            Open the <code>part_a</code> folder. Double-click
            {" "}<code style={{ background: "#0f172a", padding: "2px 6px", borderRadius: 4 }}>
              install_windows.bat
            </code>. A black command window will appear and install everything
            (takes 2–5 minutes). Wait for it to finish.
          </Step>

          <Step n={5} title="Confirm install + Desktop icon">
            At the end you should see:
            <pre
              style={{
                background: "#0f172a",
                padding: 12,
                borderRadius: 6,
                marginTop: 8,
                fontSize: 12,
                color: "#a7f3d0",
                whiteSpace: "pre-wrap",
              }}
            >
{`[PASS] no_phi_leak: OK
[OK] Desktop shortcut created: "Part A - Control Panel"
ALL GOOD! Part A is installed.`}
            </pre>
            A new icon appears on your Desktop called
            {" "}<b>"Part A - Control Panel"</b>. <b>Double-click it</b> to open
            the Control Panel.
          </Step>

          <Step n={6} title="Set your master password">
            The first time you open it, a small window asks you to set a
            <b> master password</b>. This protects your saved Dok32
            credentials. Pick something you'll remember (min 6 characters).
          </Step>

          <Step n={7} title="Tell me when done">
            Come back to this chat and write "<b>installed</b>". I'll walk you
            through adding your clinic, credentials and doing the first safe
            dry-run.
          </Step>
        </Section>

        <Section title="Safety summary">
          <ul
            style={{
              lineHeight: 1.8,
              color: "#cbd5e1",
              paddingLeft: 20,
            }}
          >
            <li>✅ Runs <b>locally</b> on your laptop — no patient data ever leaves your computer.</li>
            <li>✅ <b>Read-only</b> — the robot never saves, edits, or deletes anything in Dok32.</li>
            <li>✅ <b>Stealth + humanised timing</b> — looks like a normal employee in Chrome.</li>
            <li>✅ <b>Circuit breaker</b> — if Dok32 shows any captcha / 403 / lockout, the robot stops automatically.</li>
            <li>✅ <b>Audit log</b> — every action recorded with SHA-256 chained hash for ADHICS.</li>
          </ul>
        </Section>

        <footer
          style={{
            marginTop: 40,
            textAlign: "center",
            fontSize: 12,
            color: "#64748b",
          }}
        >
          Part A v1.0 · Confidential · For internal use of the consultancy only
        </footer>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div
      style={{
        backgroundColor: "#1e293b",
        border: "1px solid #334155",
        borderRadius: 12,
        padding: 24,
        marginBottom: 16,
      }}
    >
      <h2
        style={{
          fontSize: 18,
          fontWeight: 600,
          marginTop: 0,
          marginBottom: 16,
          color: "#f1f5f9",
        }}
      >
        {title}
      </h2>
      {children}
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div
      data-testid={`step-${n}`}
      style={{
        display: "flex",
        gap: 12,
        marginBottom: 16,
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: "50%",
          backgroundColor: "#10b981",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        {n}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontWeight: 600,
            marginBottom: 4,
            color: "#f1f5f9",
          }}
        >
          {title}
        </div>
        <div style={{ color: "#cbd5e1", fontSize: 14, lineHeight: 1.6 }}>
          {children}
        </div>
      </div>
    </div>
  );
}
